/**
 * worksheets/index.tsx — Public worksheet catalog with pagination.
 *
 * Lives at `/worksheets`. Mirrors `/games` so the Fase 4 R2 sync and
 * curriculum rollout (Task 22) have a destination ready before the
 * assets land. After the 100-item seed (N-02/N-03 prep) lands, the
 * catalog paginates 12 per page with filter chips for bagian / level /
 * konsep / status so visitors can narrow the bundle.
 *
 * The catalog reuses `publicCatalogQuery` so a single fetch powers
 * both /games and /worksheets; the static registry.json is the source
 * of truth.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { HeroBanner } from '../../components/public/HeroBanner.js';
import { WorksheetCardPublic } from '../../components/public/WorksheetCardPublic.js';
import { WorksheetPagination } from '../../components/public/WorksheetPagination.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import {
  publicCatalogQuery,
  catalogStateQuery,
  sessionQuery,
  myClassContentQuery,
  mergeCatalogWithState,
  filterWorksheets,
  paginateWorksheets,
  sortWorksheetsForCatalog,
  type WorksheetBagianNum,
  type WorksheetLevel,
  type WorksheetStatus,
} from '../../lib/api/queries.js';

const PAGE_SIZE = 12;

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  bagian: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  level: z.enum(['L1', 'L2', 'L3', 'L4']).optional(),
  konsep: z.string().optional(),
  status: z.enum(['ready', 'in_progress', 'not_yet_designed']).optional(),
});

export const Route = createFileRoute('/worksheets/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: WorksheetsCatalogPage,
});

const BAGIAN_OPTIONS: Array<{ value: WorksheetBagianNum; label: string }> = [
  { value: 1, label: 'Bagian 1' },
  { value: 2, label: 'Bagian 2' },
  { value: 3, label: 'Bagian 3' },
];

const LEVEL_OPTIONS: Array<{ value: WorksheetLevel; label: string }> = [
  { value: 'L1', label: 'L1' },
  { value: 'L2', label: 'L2' },
  { value: 'L3', label: 'L3' },
  { value: 'L4', label: 'L4' },
];

const STATUS_OPTIONS: Array<{
  value: WorksheetStatus;
  label: string;
  tone: 'mint' | 'sunrise' | 'muted';
}> = [
  { value: 'ready', label: 'Siap', tone: 'mint' },
  { value: 'in_progress', label: 'Dalam proses', tone: 'sunrise' },
  { value: 'not_yet_designed', label: 'Belum dirancang', tone: 'muted' },
];

const LEVEL_DOT_COUNT: Record<WorksheetLevel, number> = {
  L1: 1,
  L2: 2,
  L3: 3,
  L4: 4,
};

function buildHref(params: {
  page?: number;
  bagian?: WorksheetBagianNum;
  level?: WorksheetLevel;
  konsep?: string;
  status?: WorksheetStatus;
}): string {
  const search = new URLSearchParams();
  if (params.page && params.page > 1) search.set('page', String(params.page));
  if (params.bagian) search.set('bagian', String(params.bagian));
  if (params.level) search.set('level', params.level);
  if (params.konsep) search.set('konsep', params.konsep);
  if (params.status) search.set('status', params.status);
  const qs = search.toString();
  return qs ? `/worksheets?${qs}` : '/worksheets';
}

function WorksheetsCatalogPage(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const catalog = useQuery(publicCatalogQuery);
  const state = useQuery(catalogStateQuery);
  const session = useQuery(sessionQuery);

  const isLoggedIn = !!(session.data?.ok && session.data.data?.user);
  const stateMap = state.data?.ok ? (state.data.data?.worksheets ?? {}) : {};
  const worksheets = mergeCatalogWithState(
    catalog.data?.worksheets ?? [],
    stateMap,
    'worksheetId',
  );
  // N-13 Opsi A: worksheet yang dibuka tutor untuk kelas siswa dapat pill
  // "Dibuka tutor". Query hanya jalan untuk sesi login (guest = 401).
  const classContent = useQuery({ ...myClassContentQuery, enabled: isLoggedIn });
  const openedWorksheetIds = new Set<string>(
    isLoggedIn
      ? (classContent.data?.classes ?? []).flatMap((c) => c.worksheets.map((w) => w.worksheetId))
      : [],
  );
  const konsepOptions = Array.from(new Set(worksheets.map((w) => w.konsep))).sort();

  const filtered = sortWorksheetsForCatalog(filterWorksheets(worksheets, {
    bagian: search.bagian,
    level: search.level,
    konsep: search.konsep,
    status: search.status,
  }));
  const paginated = paginateWorksheets(filtered, search.page ?? 1, PAGE_SIZE);
  const totalReady = worksheets.filter((w) => w.status === 'ready').length;
  const totalInProgress = worksheets.filter((w) => w.status === 'in_progress').length;

  const activeFilters = [
    search.bagian ? `Bagian ${search.bagian}` : null,
    search.level ? search.level : null,
    search.konsep ? search.konsep : null,
    search.status ? STATUS_OPTIONS.find((s) => s.value === search.status)?.label : null,
  ].filter(Boolean) as string[];

  const setFilter = (next: Record<string, string | number | undefined>) => {
    void navigate({
      to: '/worksheets',
      search: {
        page: 1,
        bagian: search.bagian,
        level: search.level,
        konsep: search.konsep,
        status: search.status,
        ...next,
      },
    });
  };

  const clearFilters = () => {
    void navigate({ to: '/worksheets', search: { page: 1 } });
  };

  return (
    <main className="page-shell">
      <HeroBanner
        eyebrow="Off-screen learning"
        title="Worksheet Coding Class untuk latihan di rumah"
        description={`${worksheets.length} worksheet printable untuk memperkuat logika anak tanpa gawai. Cetak, kerjakan bersama — ${totalReady} siap diunduh, ${totalInProgress} dalam proses. Kode undangan dibutuhkan untuk mengunduh versi terbaru.`}
        primaryAction={
          isLoggedIn ? null : (
            <a className="button button-primary button-lg" href="/login">
              Masuk dengan kode undangan
            </a>
          )
        }
        heroArtUrl="/images/generated/worksheets-hero/worksheets-hero.png"
        heroArtAlt="Ilustrasi worksheet printable: tumpukan kertas dengan teka-teki titik-titik, pensil, dan kaca pembesar"
      />

      <section className="section worksheet-filter-shell" aria-labelledby="worksheet-filter">
        <header className="worksheet-filter-header">
          <h2 className="section-title" id="worksheet-filter">
            Filter worksheet
          </h2>
          <p className="form-message form-message-muted worksheet-filter-help">
            Saring berdasarkan bagian, level, konsep, atau status. Geser untuk melihat semua
            pilihan, ketuk untuk mengaktifkan — ketuk lagi untuk menonaktifkan.
          </p>
        </header>

        <div className="worksheet-filter-card">
          <div className="filter-cluster" role="group" aria-label="Bagian">
            <span className="filter-label">Bagian</span>
            <div className="filter-segmented" data-testid="filter-bagian-group">
              {BAGIAN_OPTIONS.map((opt) => {
                const active = search.bagian === opt.value;
                const count = worksheets.filter((w) => w.bagian === opt.value).length;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={active ? 'filter-segment is-active' : 'filter-segment'}
                    aria-pressed={active}
                    onClick={() => setFilter({ bagian: active ? undefined : opt.value })}
                    data-testid={`filter-bagian-${opt.value}`}
                  >
                    <span className="filter-segment-num">{opt.value}</span>
                    <span className="filter-segment-label">{opt.label}</span>
                    <span className="filter-segment-count" aria-label={`${count} worksheet`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-cluster" role="group" aria-label="Level">
            <span className="filter-label">Level</span>
            <div className="filter-pills" data-testid="filter-level-group">
              {LEVEL_OPTIONS.map((opt) => {
                const active = search.level === opt.value;
                const count = worksheets.filter((w) => w.level === opt.value).length;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={active ? 'filter-pill is-active' : 'filter-pill'}
                    aria-pressed={active}
                    onClick={() => setFilter({ level: active ? undefined : opt.value })}
                    data-testid={`filter-level-${opt.value}`}
                  >
                    <span className="filter-pill-dots" aria-hidden="true">
                      {Array.from({ length: 4 }, (_, i) => (
                        <span
                          key={i}
                          className={
                            i < LEVEL_DOT_COUNT[opt.value]
                              ? 'filter-pill-dot is-on'
                              : 'filter-pill-dot'
                          }
                        />
                      ))}
                    </span>
                    <span className="filter-pill-label">{opt.label}</span>
                    <span className="filter-pill-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {konsepOptions.length > 0 ? (
            <div className="filter-cluster" role="group" aria-label="Konsep">
              <span className="filter-label">Konsep</span>
              <div className="filter-tags" data-testid="filter-konsep-group">
                {konsepOptions.map((konsep) => {
                  const active = search.konsep === konsep;
                  const count = worksheets.filter((w) => w.konsep === konsep).length;
                  return (
                    <button
                      key={konsep}
                      type="button"
                      className={active ? 'filter-tag is-active' : 'filter-tag'}
                      aria-pressed={active}
                      onClick={() => setFilter({ konsep: active ? undefined : konsep })}
                      data-testid={`filter-konsep-${konsep}`}
                    >
                      <span className="filter-tag-label">{konsep}</span>
                      <span className="filter-tag-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="filter-cluster" role="group" aria-label="Status">
            <span className="filter-label">Status</span>
            <div className="filter-status-group" data-testid="filter-status-group">
              {STATUS_OPTIONS.map((opt) => {
                const active = search.status === opt.value;
                const count = worksheets.filter((w) => w.status === opt.value).length;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`filter-status is-${opt.tone}${active ? ' is-active' : ''}`}
                    aria-pressed={active}
                    onClick={() => setFilter({ status: active ? undefined : opt.value })}
                    data-testid={`filter-status-${opt.value}`}
                  >
                    <span className="filter-status-dot" aria-hidden="true" />
                    <span className="filter-status-label">{opt.label}</span>
                    <span className="filter-status-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <p
              className="form-message form-message-muted worksheet-filter-active"
              data-testid="active-filters"
            >
              <span className="worksheet-filter-active-label">Filter aktif:</span>{' '}
              {activeFilters.join(' · ')}{' '}
              <button
                type="button"
                className="link-button"
                onClick={clearFilters}
                data-testid="clear-filters"
              >
                Reset semua
              </button>
            </p>
          ) : null}
        </div>
      </section>

      <section
        className="section"
        aria-labelledby="ready-worksheets"
        id="ready-worksheets"
        data-testid="ready-worksheets-section"
      >
        <h2 className="section-title" id="ready-worksheets">
          {search.bagian || search.level || search.konsep || search.status
            ? `Hasil filter (${filtered.length})`
            : `Semua worksheet (${worksheets.length})`}
        </h2>
        {catalog.isLoading ? (
          <p className="form-message">Memuat katalog…</p>
        ) : catalog.isError ? (
          <StatePanel
            variant="error"
            title="Katalog tidak dapat dimuat"
            description="Coba segarkan halaman ini."
          />
        ) : paginated.items.length > 0 ? (
          <>
            <div className="card-grid" data-testid="worksheet-grid">
              {paginated.items.map((worksheet) => (
                <WorksheetCardPublic
                  key={worksheet.slug}
                  worksheet={worksheet}
                  isLoggedIn={isLoggedIn}
                  classOpened={openedWorksheetIds.has(worksheet.worksheetId)}
                />
              ))}
            </div>
            <WorksheetPagination
              page={paginated.page}
              pageCount={paginated.pageCount}
              buildHref={(page) =>
                buildHref({
                  page,
                  bagian: search.bagian,
                  level: search.level,
                  konsep: search.konsep,
                  status: search.status,
                })
              }
            />
            <p className="form-message form-message-muted">
              Menampilkan {paginated.items.length} dari {paginated.total} worksheet · halaman{' '}
              {paginated.page} dari {paginated.pageCount}
            </p>
          </>
        ) : (
          <StatePanel
            variant="empty"
            title="Tidak ada worksheet untuk filter ini"
            description="Coba reset filter atau pilih bagian / level yang lain."
            action={
              <button type="button" className="button button-primary" onClick={clearFilters}>
                Reset filter
              </button>
            }
          />
        )}
      </section>
    </main>
  );
}
